import type { CSSProperties } from "react";

/** 空间首页：页面底色 */
export interface SpaceHomePageConfig {
  /** 首页最外层背景色（Hero 以下露边、滚动区域底色） */
  background: string;
}

/** 空间首页：粉色基本信息区 */
export interface SpaceHomeHeroConfig {
  /** 区域高度（px，不含 safe-area；safe-area 会叠加在 paddingTop 上） */
  height: number;
  /** 顶部内边距（px，不含 safe-area） */
  paddingTop: number;
  /** 底部内边距（px） */
  paddingBottom: number;
  /** 左右内边距（px） */
  paddingX: number;
  /** 渐变背景起始色 */
  gradientFrom: string;
  /** 渐变背景结束色 */
  gradientTo: string;
}

/** 空间首页：白色底栏 */
export interface SpaceHomeSheetConfig {
  /** 顶部左右圆角（px） */
  borderRadiusTop: number;
  /** 上移与 Hero 重叠量（px），圆角需落在粉色 Hero 上才可见 */
  overlapHero: number;
  /** 背景色 */
  background: string;
}

/** 空间首页：头像 */
export interface SpaceHomeAvatarConfig {
  size: number;
  borderWidth: number;
  borderColor: string;
}

/** 空间内二级页顶栏（个人资料 / 我的订单 / 我的权益等 H5SubPageShell） */
export interface SpaceHomeSubPageShellConfig {
  /** 顶栏高度（px） */
  headerHeight: number;
  /** 顶栏左右内边距（px） */
  headerPaddingX: number;
  /** 顶栏左右占位宽度（px），与返回区对称以保持标题居中 */
  headerSideWidth: number;
  /** 标题字号（px） */
  titleFontSize: number;
}

/** 我的订单列表：订单状态配色与卡片布局 */
export interface SpaceHomeOrderListConfig {
  /** 已支付 */
  statusPaid: string;
  /** 已取消 */
  statusCancelled: string;
  /** 待支付 */
  statusUnpaid: string;
  /** 列表页背景色 */
  pageBackground: string;
  /** 订单卡片背景色 */
  cardBackground: string;
  /** 卡片间距（px） */
  cardGap: number;
  /** 卡片内边距（px） */
  cardPadding: number;
  /** 产品封面边长（px） */
  posterSize: number;
  /** 封面占位背景色 */
  posterPlaceholderBackground: string;
  /** 支付按钮描边/文字色 */
  payButtonColor: string;
  /** 金额金融字体族 */
  amountFontFamily: string;
  /** 商品单价字号（px） */
  amountFontSize: number;
  /** 合计金额字号（px，略大于单价） */
  totalAmountFontSize: number;
}

/** 空间首页：顶部栏（右上角菜单） */
export interface SpaceHomeTopBarConfig {
  /** 顶部栏距屏幕顶部的垂直距离（px，不含 safe-area；safe-area 会叠加） */
  offsetTop: number;
  /** 按钮点击热区边长（px） */
  buttonSize: number;
  /** 图标字号（px，antd icon） */
  iconSize: number;
}

/** 空间首页：名称旁认证标签 */
export interface SpaceHomeCertLabelConfig {
  /** 标签整体高度（px） */
  height: number;
  /** 文案字号（px） */
  fontSize: number;
  /** V 认证图标边长（px） */
  iconSize: number;
}

/** 空间首页：操作按钮 */
export interface SpaceHomeActionButtonConfig {
  height: number;
  borderRadius: number;
  gap: number;
}

/** 空间首页：瀑布流模块间距 */
export interface SpaceHomeFeedConfig {
  /**
   * 各模块之间的统一间距（px）
   * 直播 / 金刚区 / 教程 / 最新观点 / 权益订阅 均使用此值
   */
  sectionGap: number;
  /** 模块标题与下方内容的间距（px） */
  sectionHintMarginBottom: number;
}

/** 空间首页：教程卡片（小红书式竖版） */
export interface SpaceHomeCourseCardConfig {
  /** 封面区高度占比（%） */
  coverHeightPercent: number;
  /** 标题/播放量区高度占比（%） */
  metaHeightPercent: number;
  /** 卡片圆角（px） */
  borderRadius: number;
  /** 卡片宽高比，CSS aspect-ratio 写法，如 "3 / 4" */
  aspectRatio: string;
  /** 网格列数 */
  columns: number;
  /** 网格横向间距（px） */
  gridGapX: number;
  /** 网格纵向间距（px） */
  gridGapY: number;
  /** 下半区内边距（px） */
  metaPadding: number;
  /** 卡片整体背景色 */
  cardBackground: string;
  /** 上半封面区占位背景色（图片加载前/无图时） */
  coverBackground: string;
  /** ★ 下半部分背景色：标题 + 播放量区域（对应 .space-home-course-meta） */
  metaBackground: string;
}

/**
 * 空间首页布局配置（改数值后刷新即可，样式通过 CSS 变量注入）
 */
export const SPACE_HOME_LAYOUT = {
  page: {
    background: "rgb(250, 72, 126)",
  },
  hero: {
    height: 140,
    paddingTop: 12,
    paddingBottom: 36,
    paddingX: 16,
    gradientFrom: "rgb(216, 86, 197)",
    gradientTo: "rgb(250, 72, 126)",
  },
  sheet: {
    borderRadiusTop: 16,
    overlapHero: 16,
    background: " #fef7fd",
  },
  avatar: {
    size: 80,
    borderWidth: 3,
    borderColor: "#fff",
  },
  /** 顶部栏：右上角菜单；改 offsetTop 调节距顶垂直距离 */
  topBar: {
    offsetTop: 28,
    buttonSize: 36,
    iconSize: 20,
  },
  /** 空间二级页顶栏（返回按钮尺寸见 H5BackButtonConfig.ts） */
  subPageShell: {
    headerHeight: 48,
    headerPaddingX: 8,
    headerSideWidth: 56,
    titleFontSize: 16,
  },
  /** 我的订单列表（淘宝式卡片） */
  orderList: {
    statusPaid: "rgb(250, 72, 126)",
    statusCancelled: "#999999",
    statusUnpaid: "#fa8c16",
    pageBackground: "#f5f5f5",
    cardBackground: "#ffffff",
    cardGap: 10,
    cardPadding: 12,
    posterSize: 80,
    posterPlaceholderBackground: "rgba(22, 24, 35, 0.06)",
    payButtonColor: "#fa8c16",
    amountFontFamily:
      '"JetBrains Mono", "SFMono-Regular", ui-monospace, "Liberation Mono", Menlo, Consolas, monospace',
    amountFontSize: 14,
    totalAmountFontSize: 17,
  },
  /** 名称旁认证标签：改 height / fontSize / iconSize */
  certLabel: {
    height: 20,
    fontSize: 12,
    iconSize: 12,
  },
  actionButtons: {
    height: 38,
    borderRadius: 8,
    gap: 10,
  },
  /** 瀑布流模块间距：改 sectionGap 即可统一调节各区块距离 */
  feed: {
    sectionGap: 20,
    sectionHintMarginBottom: 8,
  },
  /**
   * 空间教程 · 小红书式竖版卡片
   * 样式变量注入见 spaceHomeLayoutCssVars() → SpaceHomeStyle.css
   */
  courseCard: {
    coverHeightPercent: 70,
    metaHeightPercent: 30,
    borderRadius: 8,
    aspectRatio: "3 / 4",
    columns: 2,
    gridGapX: 8,
    gridGapY: 12,
    metaPadding: 8,
    /** 整张卡片底色（通常与 sheet 一致） */
    cardBackground: "#fff",
    /** 上半封面占位色（无封面图 / 加载前） */
    coverBackground: "rgba(22, 24, 35, 0.06)",
    /** ★ 下半部分背景色：标题 + 播放量区域（.space-home-course-meta） */
    metaBackground: " #ffffff",
  },
} as const satisfies {
  page: SpaceHomePageConfig;
  hero: SpaceHomeHeroConfig;
  sheet: SpaceHomeSheetConfig;
  avatar: SpaceHomeAvatarConfig;
  topBar: SpaceHomeTopBarConfig;
  subPageShell: SpaceHomeSubPageShellConfig;
  orderList: SpaceHomeOrderListConfig;
  certLabel: SpaceHomeCertLabelConfig;
  actionButtons: SpaceHomeActionButtonConfig;
  feed: SpaceHomeFeedConfig;
  courseCard: SpaceHomeCourseCardConfig;
};

/** 空间二级页布局 CSS 变量（注入到 `.h5-sub-page`） */
export function spaceHomeSubPageShellCssVars(): CSSProperties {
  const { subPageShell } = SPACE_HOME_LAYOUT;
  return {
    ["--h5-sub-page-header-height" as string]: `${subPageShell.headerHeight}px`,
    ["--h5-sub-page-header-padding-x" as string]: `${subPageShell.headerPaddingX}px`,
    ["--h5-sub-page-header-side-width" as string]: `${subPageShell.headerSideWidth}px`,
    ["--h5-sub-page-title-font-size" as string]: `${subPageShell.titleFontSize}px`,
  };
}

/** 我的订单列表 CSS 变量（注入到 `.space-mine-orders`） */
export function spaceHomeOrderListCssVars(): CSSProperties {
  const { orderList } = SPACE_HOME_LAYOUT;
  return {
    ["--space-mine-order-status-paid" as string]: orderList.statusPaid,
    ["--space-mine-order-status-cancelled" as string]: orderList.statusCancelled,
    ["--space-mine-order-status-unpaid" as string]: orderList.statusUnpaid,
    ["--space-mine-order-page-bg" as string]: orderList.pageBackground,
    ["--space-mine-order-card-bg" as string]: orderList.cardBackground,
    ["--space-mine-order-card-gap" as string]: `${orderList.cardGap}px`,
    ["--space-mine-order-card-padding" as string]: `${orderList.cardPadding}px`,
    ["--space-mine-order-poster-size" as string]: `${orderList.posterSize}px`,
    ["--space-mine-order-poster-placeholder-bg" as string]: orderList.posterPlaceholderBackground,
    ["--space-mine-order-pay-btn-color" as string]: orderList.payButtonColor,
    ["--space-mine-order-amount-font-family" as string]: orderList.amountFontFamily,
    ["--space-mine-order-amount-font-size" as string]: `${orderList.amountFontSize}px`,
    ["--space-mine-order-total-amount-font-size" as string]: `${orderList.totalAmountFontSize}px`,
  };
}

/** 空间首页布局 CSS 变量（注入到 `.space-home-page`） */
export function spaceHomeLayoutCssVars(): CSSProperties {
  const { page, hero, sheet, avatar, topBar, certLabel, actionButtons, feed, courseCard } =
    SPACE_HOME_LAYOUT;
  const radius = sheet.borderRadiusTop;
  return {
    ["--space-home-page-bg" as string]: page.background,
    ["--space-home-hero-height" as string]: `${hero.height}px`,
    ["--space-home-hero-padding-top" as string]: `${hero.paddingTop}px`,
    ["--space-home-hero-padding-bottom" as string]: `${hero.paddingBottom}px`,
    ["--space-home-hero-padding-x" as string]: `${hero.paddingX}px`,
    ["--space-home-hero-gradient-from" as string]: hero.gradientFrom,
    ["--space-home-hero-gradient-to" as string]: hero.gradientTo,
    ["--space-home-sheet-radius-top" as string]: `${radius}px`,
    ["--space-home-sheet-overlap" as string]: `${sheet.overlapHero}px`,
    ["--space-home-sheet-bg" as string]: sheet.background,
    ["--space-home-avatar-size" as string]: `${avatar.size}px`,
    ["--space-home-top-bar-offset-top" as string]: `${topBar.offsetTop}px`,
    ["--space-home-top-btn-size" as string]: `${topBar.buttonSize}px`,
    ["--space-home-top-btn-icon-size" as string]: `${topBar.iconSize}px`,
    ["--space-home-cert-height" as string]: `${certLabel.height}px`,
    ["--space-home-cert-font-size" as string]: `${certLabel.fontSize}px`,
    ["--space-home-cert-icon-size" as string]: `${certLabel.iconSize}px`,
    ["--space-home-action-btn-height" as string]: `${actionButtons.height}px`,
    ["--space-home-action-btn-radius" as string]: `${actionButtons.borderRadius}px`,
    ["--space-home-action-btn-gap" as string]: `${actionButtons.gap}px`,
    ["--space-home-feed-section-gap" as string]: `${feed.sectionGap}px`,
    ["--space-home-feed-hint-margin-bottom" as string]: `${feed.sectionHintMarginBottom}px`,
    ["--space-home-course-cover-height" as string]: `${courseCard.coverHeightPercent}%`,
    ["--space-home-course-meta-height" as string]: `${courseCard.metaHeightPercent}%`,
    ["--space-home-course-radius" as string]: `${courseCard.borderRadius}px`,
    ["--space-home-course-aspect-ratio" as string]: courseCard.aspectRatio,
    ["--space-home-course-columns" as string]: String(courseCard.columns),
    ["--space-home-course-grid-gap-x" as string]: `${courseCard.gridGapX}px`,
    ["--space-home-course-grid-gap-y" as string]: `${courseCard.gridGapY}px`,
    ["--space-home-course-meta-padding" as string]: `${courseCard.metaPadding}px`,
    ["--space-home-course-card-bg" as string]: courseCard.cardBackground,
    ["--space-home-course-cover-bg" as string]: courseCard.coverBackground,
    ["--space-home-course-meta-bg" as string]: courseCard.metaBackground,
  };
}

export const SPACE_HOME_AVATAR_CONFIG = SPACE_HOME_LAYOUT.avatar;
